import { apiInstance } from '@crm/shared/api';
import { keysToCamel } from '@crm/shared/lib';

import { dealApiRoutes } from './api-routes';
import {
    CHANGE_DEAL_COLUMN_ACTION_TYPE,
    IN_WORK_OR_FINISHED_DEALS_QUERY_PARAM_API_KEYS,
    LOST_DEALS_QUERY_PARAM_API_KEYS,
    POSTPONED_DEALS_QUERY_PARAM_API_KEYS,
} from '../model/config';

import type { TDeal, TDealResponseDto } from '../model/deal';
import type {
    TChangeDealColumnPayload,
    TReturnDealsToBoardParams,
    TReturnDealsToBoardRequestDto,
} from '../model/deal-board-actions';
import type {
    TCreateDealCommentParams,
    TDealChatPollDto,
    TDeleteDealCommentParams,
} from '../model/deal-comments';
import type { TDealDetailsDto, TUpdateDealDetailsParams } from '../model/deal-details';
import type {
    TCreateDealTaskParams,
    TDealTasksDataDto,
    TDeleteDealTaskParams,
    TGetDealTasksParams,
    TSetDealTaskCompletedParams,
    TSetDealTaskCompletedRequestDto,
    TSwitchDealTaskStatusParams,
    TSwitchDealTaskStatusRequestDto,
    TUpdateDealTaskParams,
} from '../model/deal-tasks';
import type {
    TInWorkOrFinishedDealDto,
    TInWorkOrFinishedDealsDto,
    TInWorkOrFinishedDealsQueryParams,
    TMarkedInWorkOrFinishedDealDto,
    TMarkDealsInWorkOrFinishedParams,
    TMarkDealsInWorkOrFinishedRequestDto,
    TUpdateInWorkOrFinishedDealParams,
    TUpdateInWorkOrFinishedDealRequestDto,
} from '../model/in-work-or-finished-deals';
import type {
    TLostDealDto,
    TLostDealsDto,
    TLostDealsParams,
    TLostDealsQueryParams,
    TLostDealsRequestDto,
    TUpdateLostDealParams,
    TUpdateLostDealRequestDto,
} from '../model/lost-deals';
import type {
    TPostponedDealDto,
    TPostponedDealsDto,
    TPostponedDealsQueryParams,
    TPostponeDealsParams,
    TPostponeDealsRequestDto,
    TUpdatePostponedDealParams,
    TUpdatePostponedDealRequestDto,
} from '../model/postponed-deals';
import type { TBaseResponseType } from '@crm/shared/model/BaseResponse.type';

export const dealApi = {
    changeDealColumn: ({ dealId, targetColumnId, targetDealIds }: TChangeDealColumnPayload) => {
        const formData = new FormData();
        const timestamp = Date.now();
        const data = {
            'deal_id': dealId,
            'target_column_id': targetColumnId,
            'target_deal_ids': targetDealIds,
        };
        const q = [
            {
                t: timestamp,
                data: {
                    type: CHANGE_DEAL_COLUMN_ACTION_TYPE,
                    data,
                },
            },
        ];

        formData.append('t', String(timestamp));
        formData.append('q', JSON.stringify(q));

        return apiInstance.post(dealApiRoutes.changeDealColumnRoute(), {
            body: formData,
        });
    },
    deleteDeal: ({ dealId }: { dealId: TDeal['id'] }) =>
        apiInstance.delete(dealApiRoutes.deleteDealRoute(dealId)),
    getDealDetails: async (dealId: TDeal['id']) => {
        const response = await apiInstance(dealApiRoutes.getDealDetailsRoute(dealId)).json<
            TBaseResponseType<TDealDetailsDto>
        >();

        return keysToCamel(response.data);
    },
    updateDealDetails: async ({ dealId, payload }: TUpdateDealDetailsParams) => {
        const response = await apiInstance
            .patch(dealApiRoutes.updateDealDetailsRoute(dealId), {
                json: payload,
            })
            .json<TBaseResponseType<TDealDetailsDto>>();

        return keysToCamel(response.data);
    },
    createDealComment: async ({ dealId, payload }: TCreateDealCommentParams) => {
        const formData = new FormData();
        const text = payload.text.trim();

        if (text) {
            formData.append('text', text);
        }

        payload.files.forEach((file) => {
            formData.append('files[]', file);
        });

        return apiInstance.post(dealApiRoutes.createDealCommentRoute(dealId), {
            body: formData,
        });
    },
    createDealTask: async ({ dealId, payload }: TCreateDealTaskParams) =>
        apiInstance.post(dealApiRoutes.createDealTaskRoute(dealId), {
            body: payload,
        }),
    getDealTasks: async ({ dealId, payload }: TGetDealTasksParams) => {
        const response = await apiInstance
            .get(dealApiRoutes.getDealTasksRoute(dealId), {
                searchParams: {
                    direction: payload.direction,
                },
            })
            .json<TBaseResponseType<TDealTasksDataDto>>();

        return keysToCamel(response.data);
    },
    updateDealTask: async ({ dealId, payload, taskUid }: TUpdateDealTaskParams) =>
        apiInstance.post(dealApiRoutes.updateDealTaskRoute(dealId, taskUid), {
            body: payload,
        }),
    deleteDealTask: async ({ dealId, taskUid }: TDeleteDealTaskParams) =>
        apiInstance.delete(dealApiRoutes.deleteDealTaskRoute(dealId, taskUid)),
    setDealTaskCompleted: async ({ dealId, payload, taskUid }: TSetDealTaskCompletedParams) => {
        const requestPayload: TSetDealTaskCompletedRequestDto = {
            'is_completed': payload.isCompleted,
        };

        return apiInstance.post(dealApiRoutes.setDealTaskCompletedRoute(dealId, taskUid), {
            json: requestPayload,
        });
    },
    switchDealTaskStatus: async ({ dealId, payload, taskUid }: TSwitchDealTaskStatusParams) => {
        const requestPayload: TSwitchDealTaskStatusRequestDto = {
            status: payload.status,
        };

        return apiInstance.post(dealApiRoutes.switchDealTaskStatusRoute(dealId, taskUid), {
            json: requestPayload,
        });
    },
    pollDealComments: async ({
        dealId,
        stateToken,
    }: {
        dealId: TDeal['id'];
        stateToken?: string;
    }) => {
        const response = await apiInstance
            .get(dealApiRoutes.pollDealCommentsRoute(dealId), {
                searchParams: stateToken
                    ? {
                          'state_token': stateToken,
                      }
                    : undefined,
            })
            .json<TBaseResponseType<TDealChatPollDto>>();

        return keysToCamel(response.data);
    },
    deleteDealComment: ({ dealId, commentId }: TDeleteDealCommentParams) =>
        apiInstance.delete(dealApiRoutes.deleteDealCommentRoute(dealId, commentId)),
    returnDealsToBoard: async ({ dealIds, targetColumnId }: TReturnDealsToBoardParams) => {
        const requestPayload: TReturnDealsToBoardRequestDto = {
            'deal_ids': [...dealIds],
            'target_column_id': targetColumnId,
        };

        const response = await apiInstance
            .post(dealApiRoutes.returnDealsToBoardRoute(), {
                json: requestPayload,
            })
            .json<TBaseResponseType<TDealResponseDto[]>>();

        return keysToCamel(response.data);
    },
    lostDeals: async ({ dealIds, payload }: TLostDealsParams) => {
        const requestPayload: TLostDealsRequestDto = {
            'deal_ids': [...dealIds],
            ...payload,
        };

        const response = await apiInstance
            .post(dealApiRoutes.lostDealsRoute(), {
                json: requestPayload,
            })
            .json<TBaseResponseType<TDealResponseDto[]>>();

        return keysToCamel(response.data);
    },
    postponeDeals: async ({ dealIds, payload }: TPostponeDealsParams) => {
        const requestPayload: TPostponeDealsRequestDto = {
            'deal_ids': [...dealIds],
            ...payload,
        };

        const response = await apiInstance
            .post(dealApiRoutes.postponeDealsRoute(), {
                json: requestPayload,
            })
            .json<TBaseResponseType<TDealResponseDto[]>>();

        return keysToCamel(response.data);
    },
    getPostponedDeals: async ({ queryParams }: { queryParams: TPostponedDealsQueryParams }) => {
        const searchParams = new URLSearchParams();

        (Object.keys(queryParams) as (keyof TPostponedDealsQueryParams)[]).forEach((key) => {
            const value = queryParams[key];

            if (value !== null && value !== '') {
                searchParams.set(POSTPONED_DEALS_QUERY_PARAM_API_KEYS[key], String(value));
            }
        });

        const route = `${dealApiRoutes.getPostponedDealsRoute()}?${searchParams}`;
        const response = await apiInstance.get(route).json<TBaseResponseType<TPostponedDealsDto>>();

        return keysToCamel(response.data);
    },
    updatePostponedDeal: async ({ dealId, payload }: TUpdatePostponedDealParams) => {
        const requestPayload: TUpdatePostponedDealRequestDto = {
            ...(payload.requestEssence !== undefined && {
                'request_essence': payload.requestEssence,
            }),
            ...(payload.postponedUntil !== undefined && {
                'postponed_until': payload.postponedUntil,
            }),
        };

        const response = await apiInstance
            .patch(dealApiRoutes.updatePostponedDealRoute(dealId), {
                json: requestPayload,
            })
            .json<TBaseResponseType<TPostponedDealDto>>();

        return keysToCamel(response.data);
    },

    getLostDeals: async ({ queryParams }: { queryParams: TLostDealsQueryParams }) => {
        const searchParams = new URLSearchParams();

        (Object.keys(queryParams) as (keyof TLostDealsQueryParams)[]).forEach((key) => {
            const value = queryParams[key];

            if (value !== null && value !== '') {
                searchParams.set(LOST_DEALS_QUERY_PARAM_API_KEYS[key], String(value));
            }
        });

        const route = `${dealApiRoutes.getLostDealsRoute()}?${searchParams}`;
        const response = await apiInstance.get(route).json<TBaseResponseType<TLostDealsDto>>();

        return keysToCamel(response.data);
    },

    updateLostDeal: async ({ dealId, payload }: TUpdateLostDealParams) => {
        const requestPayload: TUpdateLostDealRequestDto = {
            'request_essence': payload.requestEssence,
        };

        const response = await apiInstance
            .patch(dealApiRoutes.updateLostDealRoute(dealId), {
                json: requestPayload,
            })
            .json<TBaseResponseType<TLostDealDto>>();

        return keysToCamel(response.data);
    },
    getInWorkOrFinishedDeals: async ({
        queryParams,
    }: {
        queryParams: TInWorkOrFinishedDealsQueryParams;
    }) => {
        const searchParams = new URLSearchParams();

        (Object.keys(queryParams) as (keyof TInWorkOrFinishedDealsQueryParams)[]).forEach((key) => {
            const value = queryParams[key];

            if (value !== null && value !== '') {
                searchParams.set(
                    IN_WORK_OR_FINISHED_DEALS_QUERY_PARAM_API_KEYS[key],
                    String(value)
                );
            }
        });

        const route = `${dealApiRoutes.getInWorkOrFinishedDealsRoute()}?${searchParams}`;
        const response = await apiInstance
            .get(route)
            .json<TBaseResponseType<TInWorkOrFinishedDealsDto>>();

        return keysToCamel(response.data);
    },
    updateInWorkOrFinishedDeal: async ({ dealId, payload }: TUpdateInWorkOrFinishedDealParams) => {
        const requestPayload: TUpdateInWorkOrFinishedDealRequestDto = {
            'next_contact_at': payload.nextContactAt,
        };

        const response = await apiInstance
            .patch(dealApiRoutes.updateInWorkOrFinishedDealRoute(dealId), {
                json: requestPayload,
            })
            .json<TBaseResponseType<TInWorkOrFinishedDealDto>>();

        return keysToCamel(response.data);
    },
    markDealsInWorkOrFinished: async ({ dealIds }: TMarkDealsInWorkOrFinishedParams) => {
        const requestPayload: TMarkDealsInWorkOrFinishedRequestDto = {
            'deal_ids': [...dealIds],
        };

        const response = await apiInstance
            .post(dealApiRoutes.markDealInWorkOrFinished(), {
                json: requestPayload,
            })
            .json<TBaseResponseType<TMarkedInWorkOrFinishedDealDto[]>>();

        return keysToCamel(response.data);
    },
};
